import { useEffect, useState } from 'react';
import { Sparkles, ArrowRight, Car, Users } from 'lucide-react';
import { openChatWidget } from '@/components/ai/openChatWidget';

export const EC_GREEN = '#159A47';

const SCRIPT = [
  { from: 'user', text: 'Hi! I land at DXB Terminal 3 on Friday night and need a car for 10 days, comfortable for a family of four.' },
  { from: 'ai', text: 'Welcome to Dubai! For ten days with a family of four I would suggest a spacious SUV or an executive sedan. Both are available at our Terminal 3 arrivals desk, which is open 24/7. Shall I compare the 10-day rates for you?' },
  { from: 'user', text: 'Yes please, and can I add a child seat?' },
  { from: 'ai', text: 'Of course, a child seat can be added to any booking. Here are two options with the full 10-day price, insurance and VAT included:', cards: true },
];

const CARDS = [
  { icon: Car, name: 'Spacious SUV', meta: '7 seats · Automatic · Unlimited km' },
  { icon: Users, name: 'Executive Sedan', meta: '5 seats · Automatic · Unlimited km' },
];

function Bubble({ m }) {
  const user = m.from === 'user';
  return (
    <div className={`flex ${user ? 'justify-end' : 'justify-start'} animate-fade-in`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 font-body text-sm leading-relaxed ${user ? 'bg-[#121212] text-white rounded-br-md' : 'bg-[#F1F8F3] text-[#121212] rounded-bl-md'}`}>
        {m.text}
        {m.cards && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CARDS.map((c) => (
              <div key={c.name} className="rounded-xl border border-black/[0.06] bg-white p-3">
                <c.icon className="w-4 h-4" style={{ color: EC_GREEN }} />
                <p className="mt-2 font-heading font-bold text-xs text-[#121212]">{c.name}</p>
                <p className="font-body text-[11px] text-[#666666]">{c.meta}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Typing() {
  return (
    <div className="flex justify-start animate-fade-in">
      <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-[#F1F8F3] px-4 py-3">
        {[0, 1, 2].map((i) => <span key={i} className="h-1.5 w-1.5 rounded-full bg-[#159A47] animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />)}
      </div>
    </div>
  );
}

const PHASES = SCRIPT.flatMap((m, i) => (m.from === 'ai' ? [{ count: i, typing: true, ms: 1400 }, { count: i + 1, typing: false, ms: 2400 }] : [{ count: i + 1, typing: false, ms: 1800 }]));
PHASES.push({ count: SCRIPT.length, typing: false, ms: 5000 });

function useConversation() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setPhase((p) => (p + 1) % PHASES.length), PHASES[phase].ms);
    return () => clearTimeout(t);
  }, [phase]);
  const { count, typing } = PHASES[phase];
  return { messages: SCRIPT.slice(0, count), typing };
}

export function ChatShowcase() {
  const { messages, typing } = useConversation();
  return (
    <div data-testid="ai-chat-showcase" className="relative mx-auto w-full max-w-md overflow-hidden rounded-3xl border border-black/[0.06] bg-white shadow-[0_30px_80px_rgba(21,154,71,0.18)]">
      <div className="flex items-center gap-3 px-5 py-4" style={{ backgroundColor: EC_GREEN }}>
        <span className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white"><Sparkles className="w-4 h-4" /></span>
        <div className="min-w-0">
          <p className="font-heading font-bold text-sm text-white leading-tight">Europcar AI Car Rental Concierge</p>
          <p className="flex items-center gap-1.5 font-body text-[11px] text-white/80"><span className="h-1.5 w-1.5 rounded-full bg-[#7CFC9A]" /> Online · replies in seconds</p>
        </div>
      </div>
      <div className="flex h-[420px] flex-col justify-end space-y-3 overflow-hidden px-4 py-5 sm:px-5">
        {messages.map((m, i) => <Bubble key={i} m={m} />)}
        {typing && <Typing />}
      </div>
      <button type="button" data-testid="ai-showcase-cta" onClick={openChatWidget}
        className="flex w-full items-center justify-between gap-3 border-t border-black/[0.06] bg-[#FAFAFA] px-5 py-4 text-left transition-colors hover:bg-[#F1F8F3]">
        <span className="font-heading font-bold text-sm text-[#121212]">Continue with the real concierge</span>
        <span className="grid h-9 w-9 place-items-center rounded-full text-white" style={{ backgroundColor: EC_GREEN }}><ArrowRight className="w-4 h-4" /></span>
      </button>
    </div>
  );
}
